import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Loading Spinner Component
 */
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

/**
 * ProtectedRoute Component
 *
 * Protects routes that require authentication
 * Shows loading spinner while checking auth status
 * Redirects to login if not authenticated
 * Preserves the attempted URL for redirect after login
 *
 * Usage:
 * <Route path="/admin/dashboard" element={
 *   <ProtectedRoute>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while validating token
  if (loading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, redirect to login
  // Save the current location so we can redirect back after login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected component
  return children;
};

/**
 * PublicRoute Component
 *
 * For routes like login that should redirect to dashboard if already authenticated
 *
 * Usage:
 * <Route path="/admin/login" element={
 *   <PublicRoute>
 *     <AdminLogin />
 *   </PublicRoute>
 * } />
 */
export const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return <LoadingSpinner />;
  }

  // If authenticated, redirect to where they were trying to go or dashboard
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/admin/dashboard';
    return <Navigate to={from} replace />;
  }

  // Not authenticated, show the public page (like login)
  return children;
};

export default ProtectedRoute;
